<template>
  <div>
    <div class="pb-3">
      <button
        v-if="selectedFolder !== 'all'"
        type="button"
        class="crumb-link"
        @click="onFolderChange('all')"
      >
        <b-icon icon="chevron-left" font-scale="0.8" />
        All files
      </button>
      <h4 class="current-folder-label">
        {{ currentFolderLabel }}
        <span v-if="fileCountLabel" class="folder-count">
          {{ fileCountLabel }}
        </span>
      </h4>
      <p v-if="selectedFolder === 'recent'" class="folder-note">
        Files added in the last 7 days
      </p>
    </div>
    <file-toolbar
      :search="search"
      :can-upload="canUpload"
      :can-add-folder="canAddFolder"
      @search-change="onSearchChange"
      @new-folder="$emit('new-folder', openFolder)"
      @upload="$emit('upload')"
    />
    <keep-alive>
      <file-filters
        class="mt-3 mb-3"
        :selected-type="selectedType"
        :counts="typeCounts"
        @type-change="onTypeChange"
      />
    </keep-alive>
    <file-folders
      :selected-folder="selectedFolder"
      :folders="folders"
      :can-manage="canManageFolders"
      @folder-change="onFolderChange"
      @suggest-folders="$emit('suggest-folders')"
      @new-folder="$emit('new-folder', $event)"
    />
    <file-table
      class="mt-3"
      :files="files"
      :folders="folders"
      :can-organise="canManageFolders"
      :selected-folder="selectedFolder"
      :loading="loading"
      :empty-message="emptyMessage"
      :filesMeta="filesMeta"
      :currentPage="currentPage"
      :selection-key="selectionKey"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :load-all-rows="loadAllRows"
      @page-change="onPageChange"
      @sort-change="$emit('sort-change', $event)"
      @bulk-download="$emit('bulk-download', $event)"
      @bulk-share="$emit('bulk-share', $event)"
      @bulk-delete="$emit('bulk-delete', $event)"
      @bulk-folder="$emit('bulk-folder', $event)"
      @set-folders="$emit('set-folders', $event)"
      @open-file="$emit('open-file', $event)"
      @download-file="$emit('download-file', $event)"
      @share-file="$emit('share-file', $event)"
      @edit-file="$emit('edit-file', $event)"
      @delete-file="$emit('delete-file', $event)"
    />
  </div>
</template>

<script>
import FileToolbar from '@/components/files/FileToolbar'
import FileFilters from '@/components/files/FileFilters'
import FileFolders from '@/components/files/FileFolders'
import FileTable from '@/components/files/FileTable'
import { MAX_FOLDER_LEVELS } from '@/utils/folderTree'

export default {
  components: {
    FileToolbar,
    FileFilters,
    FileFolders,
    FileTable,
  },

  props: {
    selectedFolder: { type: String, default: 'all' },
    selectedType: { type: String, default: 'all' },
    search: { type: String, default: '' },
    canUpload: { type: Boolean, default: false },
    folders: { type: Array, default: () => [] },
    canManageFolders: { type: Boolean, default: true },
    currentFolderLabel: { type: String, default: '' },
    files: { type: Array, default: () => [] },
    filesMeta: { type: Object, default: () => ({}) },
    currentPage: { type: Number, default: 1 },
    loading: { type: Boolean, default: false },
    emptyMessage: { type: String, default: '' },
    typeCounts: { type: Object, default: null },
    sortBy: { type: String, default: 'added' },
    sortDesc: { type: Boolean, default: true },
    loadAllRows: { type: Function, default: null },
  },

  computed: {
    // The folder that is open, or undefined on All files and the other views.
    openFolder() {
      return this.folders.find((folder) => folder.id === this.selectedFolder)
    },

    // "New folder" in the toolbar: on All files, or inside an open folder that isn't on the deepest level.
    canAddFolder() {
      if (!this.canManageFolders) return false
      // With no folders yet, All files has its own "New folder" button.
      if (this.selectedFolder === 'all') return this.folders.length > 0
      return !!this.openFolder && this.openFolder.depth + 1 < MAX_FOLDER_LEVELS
    },

    selectionKey() {
      return [
        this.selectedFolder,
        this.selectedType,
        this.search,
        this.sortBy,
        this.sortDesc,
      ].join('|')
    },

    fileCountLabel() {
      const total = this.filesMeta && this.filesMeta.total
      if (this.loading || total === undefined || total === null) return ''
      return `${total} ${total === 1 ? 'file' : 'files'}`
    },
  },

  methods: {
    onFolderChange(folderId) {
      this.$emit('folder-change', folderId)
    },

    onTypeChange(typeId) {
      this.$emit('type-change', typeId)
    },

    onSearchChange(value) {
      this.$emit('search-change', value)
    },

    onPageChange(page) {
      this.$emit('page-change', page)
    },
  },
}
</script>

<style scoped>
.crumb-link {
  display: inline-flex;
  align-items: center;
  margin-bottom: 4px;
  padding: 0;
  background: transparent;
  border: 0;
  color: #006cff;
  font-size: 14px;
}

.crumb-link svg {
  margin-right: 4px;
}

.crumb-link:hover {
  text-decoration: underline;
}

.folder-note {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 14px;
}

.folder-count {
  margin-left: 8px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 400;
}

.current-folder-label {
  margin-bottom: 0;
  color: #111827;
  font-weight: 700;
}
</style>
